/**
 * usePaywallDetection - Hook to detect when payment paywall should be shown
 *
 * Uses the dedicated paywall-context endpoint that consolidates all needed data
 * without requiring access to protected endpoints (which return 403 for cancelled users).
 *
 * Returns flowType to indicate which payment flow is needed:
 * - 'update_payment_method': Existing subscription with payment issue (SetupIntent)
 * - 'create_subscription': No active subscription, need to resubscribe (PaymentIntent)
 *
 * Returns context for message display based on:
 * - Trial vs Subscription
 * - Failed Payment vs Cancelled
 * - Labs processed vs No labs
 */

import { useGetPaywallContextV1SubscriptionsPaywallContextGet } from '@/api/generated-catch-backend/subscriptions/subscriptions';
import type { PaywallPriceResponse } from '@/api/generated-catch-backend/model';
import { useMe } from '@/context/me/me.context';
import { UserLevelType } from '@/repositories/me.repository';
import type { PaywallContext } from './components/PaywallHeader';

export type PaywallFlowType = 'update_payment_method' | 'create_subscription';

export interface UsePaywallDetectionResult {
  shouldShowPaywall: boolean;
  context: PaywallContext | null;
  subscriptionId: string | null;
  isLoading: boolean;
  isInDunning: boolean;
  /** Which payment flow is needed */
  flowType: PaywallFlowType | null;
  /** Price data from paywall context */
  price: PaywallPriceResponse | null;
  /** Lab order ID for downloading raw results PDF (when hasLabsProcessed is true) */
  labOrderId: string | null;
}

// Subscription statuses that should trigger the paywall
const PAYWALL_STATUSES = ['past_due', 'unpaid', 'canceled', 'incomplete'];

export function usePaywallDetection(): UsePaywallDetectionResult {
  const { data: userData } = useMe();
  const { data: paywallContext, isLoading: isContextLoading } = useGetPaywallContextV1SubscriptionsPaywallContextGet();

  const userLevel = userData?.userLevel;
  const subscriptionStatus = paywallContext?.subscriptionStatus?.toLowerCase() ?? null;
  // stripeSubscriptionId is needed for update_payment_method flow to create SetupIntent
  const subscriptionId = paywallContext?.stripeSubscriptionId ?? null;
  // isInDunning indicates payment failure (from dunning cycle data)
  const isInDunning = paywallContext?.isInDunning ?? false;
  // wasTrial indicates subscription ever had a trial period (trial_end_utc is set)
  // This remains true even after trial ends and becomes a full subscription
  const wasTrial = paywallContext?.wasTrial ?? false;
  // hasLabsProcessed from paywall context (boolean, not the actual lab data)
  const hasLabsProcessed = paywallContext?.hasLabsProcessed ?? false;
  // Lab order ID for downloading raw results PDF
  const labOrderId = paywallContext?.labOrderId ?? null;
  // Price data from paywall context
  const price = paywallContext?.price ?? null;

  // Use wasTrial from API (based on trial_end_utc in DB), not current status
  const isTrial = wasTrial;

  // Don't show paywall while loading
  if (isContextLoading || !userData) {
    return {
      shouldShowPaywall: false,
      context: null,
      subscriptionId: null,
      isLoading: true,
      isInDunning: false,
      flowType: null,
      price: null,
      labOrderId: null,
    };
  }

  // Priority 1: Check isInDunning flag (payment failure with dunning cycle in DB)
  // This is the most reliable indicator of payment failure
  // However, if the subscription is already canceled in Stripe, we need create_subscription flow
  if (isInDunning) {
    // If Stripe subscription is canceled, we can't update it - need to create new
    const isCanceled = subscriptionStatus === 'canceled';
    return {
      shouldShowPaywall: true,
      context: {
        isTrial,
        isPaymentFailed: true,
        hasLabsProcessed,
      },
      subscriptionId,
      isLoading: false,
      isInDunning: !isCanceled, // Not really in dunning if already canceled
      flowType: isCanceled ? 'create_subscription' : 'update_payment_method',
      price,
      labOrderId,
    };
  }

  // Priority 2: Check subscription status for payment issues
  // Flow: Update payment method on existing subscription
  if (subscriptionStatus && (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid')) {
    return {
      shouldShowPaywall: true,
      context: {
        isTrial,
        isPaymentFailed: true,
        hasLabsProcessed,
      },
      subscriptionId,
      isLoading: false,
      isInDunning: false,
      flowType: 'update_payment_method',
      price,
      labOrderId,
    };
  }

  // Priority 3: Check userLevel from /me for canceled state (deliberate cancellation)
  // Flow: Create new subscription (no active subscription exists)
  if (userLevel === UserLevelType.canceled) {
    return {
      shouldShowPaywall: true,
      context: {
        isTrial,
        isPaymentFailed: false, // Cancelled, not payment failed
        hasLabsProcessed,
      },
      subscriptionId,
      isLoading: false,
      isInDunning: false,
      flowType: 'create_subscription',
      price,
      labOrderId,
    };
  }

  // Priority 4: Check subscription status for canceled/incomplete
  if (subscriptionStatus && PAYWALL_STATUSES.includes(subscriptionStatus)) {
    const isPaymentFailed = subscriptionStatus !== 'canceled';
    // Canceled = create new subscription, incomplete = update payment method
    const flowType: PaywallFlowType = subscriptionStatus === 'canceled' ? 'create_subscription' : 'update_payment_method';
    return {
      shouldShowPaywall: true,
      context: {
        isTrial,
        isPaymentFailed,
        hasLabsProcessed,
      },
      subscriptionId,
      isLoading: false,
      isInDunning: false,
      flowType,
      price,
      labOrderId,
    };
  }

  // No paywall needed
  return {
    shouldShowPaywall: false,
    context: null,
    subscriptionId,
    isLoading: false,
    isInDunning: false,
    flowType: null,
    price,
    labOrderId,
  };
}
